import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  cancelPlot,
  processRefund,
  getCanceledPlots,
  getRefundInstallments,
  updateRefundInstallment,
  getCancelPlotDetails,

} from '../../api'

// Async Thunks
export const cancelPlotThunk = createAsyncThunk(
  'cancelPlot/cancel',
  async (plotData, { rejectWithValue }) => {
    try {
      return await cancelPlot(plotData)
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const processRefundThunk = createAsyncThunk(
  'cancelPlot/processRefund',
  async (refundData, { rejectWithValue }) => {
    try {
      return await processRefund(refundData)
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const getCanceledPlotsThunk = createAsyncThunk(
  'cancelPlot/getCanceledPlots',
  async (filters, { rejectWithValue }) => {
    try {
      return await getCanceledPlots(filters)
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const getRefundInstallmentsThunk = createAsyncThunk(
  'cancelPlot/getRefundInstallments',
  async (params, { rejectWithValue }) => {
    try {
      // Extract cancelPlotId from params object or use directly if it's a string
      const cancelPlotId =
        typeof params === 'object' && params.cancelPlotId
          ? params.cancelPlotId
          : params

      // Ensure cancelPlotId is a string
      if (!cancelPlotId || typeof cancelPlotId !== 'string') {
        throw new Error('Invalid Cancel Plot ID')
      }

      // console.log('Fetching refund installments for ID:', cancelPlotId)
      const result = await getRefundInstallments(cancelPlotId)
      // console.log('API Response for refund installments:', result)
      return result
    } catch (err) {
      console.error('Error fetching refund installments:', err)
      return rejectWithValue(err)
    }
  }
)

export const updateRefundInstallmentThunk = createAsyncThunk(
  'cancelPlot/updateRefundInstallment',
  async ({ installmentId, updateData }, { rejectWithValue }) => {
    try {
      return await updateRefundInstallment(installmentId, updateData)
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const getCancelPlotDetailsThunk = createAsyncThunk(
  'cancelPlot/getCancelPlotDetails',
  async (cancelPlotId, { rejectWithValue }) => {
    try {
      return await getCancelPlotDetails(cancelPlotId)
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)



// Helper function to calculate statistics from refund installments
const calculateStatistics = installments => {
  if (!Array.isArray(installments) || installments.length === 0) {
    return {
      totalPaid: 0,
      totalPending: 0,
      paidInstallments: 0,
      pendingInstallments: 0
    }
  }

  const paidInstallments = installments.filter(inst => inst.is_paid)
  const pendingInstallments = installments.filter(inst => !inst.is_paid)

  return {
    totalPaid: paidInstallments.reduce(
      (sum, inst) => sum + (Number(inst.amount) || 0),
      0
    ),
    totalPending: pendingInstallments.reduce(
      (sum, inst) => sum + (Number(inst.amount) || 0),
      0
    ),
    paidInstallments: paidInstallments.length,
    pendingInstallments: pendingInstallments.length
  }
}

// Initial state
const initialState = {
  canceledPlots: [],
  selectedPlotDetails: null,
  refundInstallments: [],
  loading: false,
  deleting: false,
  error: null,
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0
  },
  statistics: {
    totalPaid: 0,
    totalPending: 0,
    paidInstallments: 0,
    pendingInstallments: 0
  },
  globalStatistics: {
    totalSocietyProfit: 0,
    totalRefunded: 0,
    totalRemaining: 0,
    count: 0
  }
}

// Slice
const cancelPlotSlice = createSlice({
  name: 'cancelPlot',
  initialState,
  reducers: {
    resetCancelPlotState: () => initialState,
    // Add reducer to update statistics manually if needed
    updateStatistics: state => {
      state.statistics = calculateStatistics(state.refundInstallments)
    },
    // Clear selected plot details
    clearSelectedPlotDetails: state => {
      state.selectedPlotDetails = null
      state.refundInstallments = []
      state.statistics = {
        totalPaid: 0,
        totalPending: 0,
        paidInstallments: 0,
        pendingInstallments: 0
      }
    }
  },
  extraReducers: builder => {
    builder
      // Cancel Plot
      .addCase(cancelPlotThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(cancelPlotThunk.fulfilled, (state, action) => {
        state.loading = false
        // Immediately add the newly canceled plot to the list without refreshing
        if (action.payload && action.payload.cancelPlot) {
          const newPlot = action.payload.cancelPlot
          // Check if plot already exists to avoid duplicates
          const exists = state.canceledPlots.some(
            plot => plot._id && plot._id.toString() === newPlot._id.toString()
          )
          if (!exists) {
            // Add to the beginning of the array (most recent first)
            state.canceledPlots = [newPlot, ...state.canceledPlots]
          }
        }
      })
      .addCase(cancelPlotThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Process Refund
      .addCase(processRefundThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(processRefundThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update the canceled plot in the list if refund was processed
        if (action.payload && action.payload.cancelPlotId) {
          const plotIndex = state.canceledPlots.findIndex(
            plot => plot._id && plot._id.toString() === action.payload.cancelPlotId.toString()
          )
          if (plotIndex !== -1 && action.payload.updatedPlot) {
            state.canceledPlots[plotIndex] = action.payload.updatedPlot
          }
        }
      })
      .addCase(processRefundThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Get Canceled Plots
      .addCase(getCanceledPlotsThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getCanceledPlotsThunk.fulfilled, (state, action) => {
        state.loading = false

        let plots = []
        if (action.payload && action.payload.canceledPlots) {
          plots = Array.isArray(action.payload.canceledPlots)
            ? action.payload.canceledPlots
            : []
        } else if (Array.isArray(action.payload)) {
          plots = action.payload
        }

        // Update pagination if present
        if (action.payload && action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }

        // Update aggregate statistics if present
        if (action.payload && action.payload.stats) {
          state.globalStatistics = action.payload.stats;
        }

        // Remove duplicates by _id to prevent duplicate entries
        const uniquePlotsMap = new Map()
        plots.forEach(plot => {
          if (plot && plot._id) {
            uniquePlotsMap.set(plot._id.toString(), plot)
          }
        })

        state.canceledPlots = Array.from(uniquePlotsMap.values())
      })
      .addCase(getCanceledPlotsThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        // Reset to empty array on error
        state.canceledPlots = []
      })

      // Get Refund Installments
      .addCase(getRefundInstallmentsThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getRefundInstallmentsThunk.fulfilled, (state, action) => {
        state.loading = false

        // Handle different API response formats
        let installments = []

        if (action.payload && Array.isArray(action.payload)) {
          // Direct array response
          installments = action.payload
        } else if (
          action.payload &&
          action.payload.installments &&
          Array.isArray(action.payload.installments)
        ) {
          // Nested installments array
          installments = action.payload.installments
        } else if (action.payload && typeof action.payload === 'object') {
          // Convert object to array if needed
          installments = Object.values(action.payload).filter(
            item => item && typeof item === 'object'
          )
        }

        // console.log('Processed installments:', installments)

        // Ensure refundInstallments is always an array
        state.refundInstallments = installments

        // Calculate statistics based on installments
        state.statistics = calculateStatistics(installments)
        // console.log('Calculated statistics:', state.statistics)

        // If no data received, check if we should use mock data for development
        if (
          installments.length === 0 &&
          process.env.NODE_ENV === 'development'
        ) {
          // console.log('Using mock data for development')
          const mockData = [
            {
              _id: 'mock1',
              amount: 50000,
              due_date: '2025-06-01',
              is_paid: false,
              payment_date: null,
              payment_method: 'Bank Transfer',
              updated_at: '2025-05-01'
            },
            {
              _id: 'mock2',
              amount: 50000,
              due_date: '2025-07-01',
              is_paid: false,
              payment_date: null,
              payment_method: 'Bank Transfer',
              updated_at: '2025-05-01'
            },
            {
              _id: 'mock3',
              amount: 50000,
              due_date: '2025-05-01',
              is_paid: true,
              payment_date: '2025-05-01',
              payment_method: 'Bank Transfer',
              updated_at: '2025-05-01'
            }
          ]

          state.refundInstallments = mockData
          state.statistics = calculateStatistics(mockData)
        }
      })
      .addCase(getRefundInstallmentsThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        // Reset to empty array on error
        state.refundInstallments = []
        // Reset statistics
        state.statistics = {
          totalPaid: 0,
          totalPending: 0,
          paidInstallments: 0,
          pendingInstallments: 0
        }
        console.error('Error in getRefundInstallmentsThunk:', action.payload)
      })

      // Update Refund Installment
      .addCase(updateRefundInstallmentThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateRefundInstallmentThunk.fulfilled, (state, action) => {
        state.loading = false

        // Update the specific installment in the array
        if (action.payload && action.payload._id) {
          const index = state.refundInstallments.findIndex(
            inst => inst._id === action.payload._id
          )

          if (index !== -1) {
            state.refundInstallments[index] = action.payload
            // Recalculate statistics after update
            state.statistics = calculateStatistics(state.refundInstallments)
          }
        }
      })
      .addCase(updateRefundInstallmentThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Get Cancel Plot Details
      .addCase(getCancelPlotDetailsThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getCancelPlotDetailsThunk.fulfilled, (state, action) => {
        state.loading = false
        state.selectedPlotDetails = action.payload
      })
      .addCase(getCancelPlotDetailsThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.selectedPlotDetails = null
      })


  }
})

// Export actions and reducer
export const {
  resetCancelPlotState,
  updateStatistics,
  clearSelectedPlotDetails
} = cancelPlotSlice.actions

// Selectors
export const selectCanceledPlots = state => state.cancelPlot.canceledPlots
export const selectSelectedPlotDetails = state =>
  state.cancelPlot.selectedPlotDetails
export const selectRefundInstallments = state =>
  state.cancelPlot.refundInstallments
export const selectCancelPlotLoading = state => state.cancelPlot.loading
export const selectCancelPlotError = state => state.cancelPlot.error
export const selectRefundStatistics = state => state.cancelPlot.statistics
export const selectGlobalStatistics = state => state.cancelPlot.globalStatistics
export const selectPagination = state => state.cancelPlot.pagination

export default cancelPlotSlice.reducer
